import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { MapPin, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { startPolling } from '@/utils/layoutPolling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pop up muncul lagi tiap 30 menit selama belum dikonfirmasi.
const SNOOZE_MS = 30 * 60 * 1000;
// Cek ke server tidak perlu sering, cukup 2 menit sekali.
const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const SNOOZE_KEY = 'vaxa_addr_change_snooze';

const readSnooze = () => {
  try {
    return JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}') || {};
  } catch (_e) {
    return {};
  }
};

const writeSnooze = (map) => {
  try {
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(map));
  } catch (_e) {
    /* abaikan */
  }
};

const fmtTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('id-ID', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta',
  }) + ' WIB';
};

// Format "2026-10-20" + "07:45" menjadi "20 Okt 2026, 07:45 WIB"
const fmtDeliver = (date, time) => {
  if (!date) return '-';
  const d = new Date(`${date}T${time || '00:00'}:00+07:00`);
  if (isNaN(d.getTime())) return `${date} ${time || ''}`.trim();
  return `${d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Jakarta' })}, ${time || ''} WIB`;
};

const itemKey = (it) => `${it.kind}:${it.request_id}`;

/**
 * Okt 2026 — Pop up khusus akun gudang: alamat atau jam pengiriman terjadwal berubah.
 * Muncul sampai gudang menekan "Saya sudah tahu". Kalau ditutup tanpa konfirmasi,
 * muncul lagi 30 menit kemudian (tetap tampil sebagai belum dikonfirmasi di admin dan marketing).
 */
export default function AddressChangePopup() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [acking, setAcking] = useState(null);
  const itemsRef = useRef([]);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const evaluate = useCallback((list) => {
    const snooze = readSnooze();
    const now = Date.now();
    // Bersihkan catatan tunda untuk request yang sudah tidak pending
    const ids = new Set(list.map((x) => x.request_id));
    const cleaned = {};
    Object.keys(snooze).forEach((k) => { if (ids.has(k)) cleaned[k] = snooze[k]; });
    if (Object.keys(cleaned).length !== Object.keys(snooze).length) writeSnooze(cleaned);
    const due = list.some((x) => {
      const until = cleaned[x.request_id];
      // Perubahan yang lebih baru dari waktu tunda harus langsung muncul
      const changedAt = x.changed_at ? new Date(x.changed_at).getTime() : 0;
      return !until || until <= now || (changedAt && changedAt > until - SNOOZE_MS);
    });
    setOpen(due);
  }, []);

  const fetchPending = useCallback(async () => {
    const res = await axios.get(`${API}/scheduled-deliveries/gudang-changes/pending`, {
      headers: headers(),
      timeout: 20000,
    });
    const list = [
      ...(res.data?.address || []).map((x) => ({ ...x, kind: 'ADDRESS' })),
      ...(res.data?.schedule || []).map((x) => ({ ...x, kind: 'SCHEDULE' })),
    ];
    itemsRef.current = list;
    setItems(list);
    evaluate(list);
  }, [evaluate]);

  useEffect(() => {
    const stop = startPolling(fetchPending, { intervalMs: CHECK_INTERVAL_MS });
    return () => { if (typeof stop === 'function') stop(); };
  }, [fetchPending]);

  const handleLater = () => {
    const snooze = readSnooze();
    const until = Date.now() + SNOOZE_MS;
    itemsRef.current.forEach((x) => { snooze[x.request_id] = until; });
    writeSnooze(snooze);
    setOpen(false);
  };

  const handleAck = async (item) => {
    setAcking(itemKey(item));
    try {
      const path = item.kind === 'SCHEDULE' ? 'schedule-change' : 'address-change';
      await axios.post(`${API}/supply-requests/${item.request_id}/${path}/ack`, {}, { headers: headers() });
      toast.success(item.kind === 'SCHEDULE' ? 'Terima kasih, perubahan jam sudah dicatat' : 'Terima kasih, perubahan alamat sudah dicatat');
      await fetchPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan konfirmasi');
    } finally {
      setAcking(null);
    }
  };

  if (!open || items.length === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4"
      data-testid="address-change-popup"
    >
      <div className="bg-white rounded-lg p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold flex items-center gap-2 text-red-700 mb-1">
          <MapPin className="w-5 h-5" />
          Pengiriman terjadwal berubah
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Cek perubahan alamat atau jam di bawah. Tekan "Saya sudah tahu" kalau sudah dibaca.
        </p>

        <div className="space-y-3">
          {items.map((it) => {
            const ch = it;
            const isSchedule = it.kind === 'SCHEDULE';
            return (
              <div
                key={itemKey(it)}
                className={`border rounded-lg p-3 text-sm ${(ch.shipped_when_changed || it.shipped_now) ? 'border-red-400 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
              >
                {(ch.shipped_when_changed || it.shipped_now) && (
                  <p className="flex items-center gap-1 font-bold text-red-700 mb-1">
                    <AlertTriangle className="w-4 h-4" /> BARANG SUDAH BERANGKAT. Segera cek posisi barang.
                  </p>
                )}
                <p className="font-semibold text-gray-900 flex items-center gap-1">
                  {isSchedule ? <Clock className="w-4 h-4 text-amber-700" /> : <MapPin className="w-4 h-4 text-amber-700" />}
                  {isSchedule ? 'Jam berubah' : 'Alamat berubah'}
                </p>
                <p className="font-semibold text-gray-900">
                  {it.request_id} · Order {it.order_id || '-'}
                </p>
                <p className="text-gray-600">Diubah {fmtTime(ch.changed_at)} oleh {ch.changed_by_name || '-'}</p>
                {isSchedule ? (
                  <>
                    <p className="text-gray-600">
                      Jam layanan sekarang: {ch.new_service_date || '-'} {ch.new_service_time || ''}
                    </p>
                    <p className="text-gray-600">Dulu harus sampai: {fmtDeliver(ch.old_deliver_date, ch.old_deliver_time)}</p>
                    <p className="text-gray-900 font-medium">
                      Sekarang harus sampai: {fmtDeliver(ch.new_deliver_date, ch.new_deliver_time)}
                    </p>
                  </>
                ) : (
                  <>
                    {ch.old_address && <p className="text-gray-600 break-words">Dulu: {ch.old_address}</p>}
                    <p className="text-gray-900 font-medium break-words">Sekarang: {ch.new_address || '-'}</p>
                    {ch.new_maps_url && (
                      <a href={ch.new_maps_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">
                        Buka di Google Maps
                      </a>
                    )}
                  </>
                )}
                <div className="flex items-center justify-between gap-2 mt-3">
                  <Link
                    to={`/gudang/pengiriman/${it.request_id}`}
                    onClick={() => setOpen(false)}
                    className="text-xs text-blue-700 underline"
                  >
                    Buka detail
                  </Link>
                  <Button
                    size="sm"
                    onClick={() => handleAck(it)}
                    disabled={acking === itemKey(it)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid={`${isSchedule ? 'schedule' : 'address'}-ack-${it.request_id}`}
                  >
                    {acking === itemKey(it) ? 'Menyimpan...' : 'Saya sudah tahu'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleLater}
          className="mt-4 w-full text-sm text-gray-500 underline"
          data-testid="address-change-later"
        >
          Nanti (ingatkan lagi 30 menit)
        </button>
      </div>
    </div>
  );
}
